import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

export default function ContactSection() {
  const { t } = useTranslation('common')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [charCount, setCharCount] = useState(0)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]')
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setStatus('success')
      return
    }

    const messageEl = form.querySelector<HTMLTextAreaElement>('textarea[name="message"]')
    if (messageEl && messageEl.value.length > 500) {
      setErrorMsg('留言内容不能超过500个字符')
      setStatus('error')
      return
    }

    setStatus('loading')
    setErrorMsg('')

    try {
      const formData = new FormData(form)
      const response = await fetch('https://readdy.ai/api/form/d9rg6gfnahb7382ovm50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      })

      const responseText = await response.text()
      let parsed: Record<string, unknown> = {}
      try {
        parsed = JSON.parse(responseText)
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success')
        form.reset()
        setCharCount(0)
      } else {
        const serverMsg = (parsed as Record<string, unknown>)?.meta && typeof (parsed as Record<string, unknown>).meta === 'object'
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>)?.message || responseText
          : responseText
        setErrorMsg(typeof serverMsg === 'string' ? serverMsg : t('about.formError'))
        setStatus('error')
      }
    } catch {
      setErrorMsg(t('about.formError'))
      setStatus('error')
    }
  }

  const subjects = [
    { value: 'general', label: t('about.subjectGeneral') },
    { value: 'cooperation', label: t('about.subjectCooperation') },
    { value: 'creator', label: t('about.subjectCreator') },
    { value: 'feedback', label: t('about.subjectFeedback') },
    { value: 'other', label: t('about.subjectOther') },
  ]

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-3">
            {t('about.contactTitle')}
          </h2>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            {t('about.contactSubtitle')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 md:gap-16 max-w-5xl mx-auto">
          <div className="lg:w-2/5">
            <h3 className="font-heading text-lg text-foreground-950 mb-6">
              {t('about.contactInfo')}
            </h3>
            <div className="flex flex-col gap-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-500"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">邮箱</div>
                  <div className="text-sm text-foreground-500">{t('footer.email')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-primary-500"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">电话</div>
                  <div className="text-sm text-foreground-500">{t('footer.phone')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-500"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">地址</div>
                  <div className="text-sm text-foreground-500">{t('footer.address')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-primary-500"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">工作时间</div>
                  <div className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-3/5">
            <form onSubmit={handleSubmit} data-readdy-form="" className="bg-background-100 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading text-lg text-foreground-950 mb-6">
                {t('about.sendMessage')}
              </h3>

              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="form-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('about.formName')}
                    </label>
                    <input
                      id="form-name"
                      type="text"
                      name="name"
                      required
                      placeholder={t('about.formNamePlaceholder')}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="form-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('about.formEmail')}
                    </label>
                    <input
                      id="form-email"
                      type="email"
                      name="email"
                      required
                      placeholder={t('about.formEmailPlaceholder')}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="form-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('about.formSubject')}
                  </label>
                  <select
                    id="form-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors appearance-none"
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23888' d='M12 15l-8-8h16z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '2.5rem' }}
                  >
                    <option value="">{t('about.formSubjectPlaceholder')}</option>
                    {subjects.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="form-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('about.formMessage')}
                  </label>
                  <textarea
                    id="form-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder={t('about.formMessagePlaceholder')}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  ></textarea>
                  <div className="text-xs text-foreground-400 mt-1 text-right">{charCount}/500</div>
                </div>

                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      发送中...
                    </span>
                  ) : t('about.formSubmit')}
                </button>
              </div>

              {status === 'success' && (
                <div className="mt-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  {t('about.formSuccess')}
                </div>
              )}

              {status === 'error' && errorMsg && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {errorMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}